import React, { useEffect, useState, useRef } from 'react';
import { ArrowLeftIcon, CheckIcon, AlertCircleIcon, MapPinIcon, PackageIcon, PlusIcon, MinusIcon, CheckCircleIcon } from 'lucide-react';
import { MaterialItem } from './MaterialPreparation';
interface MaterialScanningInterfaceProps {
  material: MaterialItem;
  onScanComplete: (scannedCount: number) => void;
  onBack: () => void;
}
export function MaterialScanningInterface({
  material,
  onScanComplete,
  onBack
}: MaterialScanningInterfaceProps) {
  const [scannedCode, setScannedCode] = useState('');
  const [scanSuccess, setScanSuccess] = useState<boolean | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [manualCount, setManualCount] = useState(1);
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [scannedPallets, setScannedPallets] = useState<string[]>([]);
  const [showSuccessFeedback, setShowSuccessFeedback] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  // Überprüfen, ob alle Paletten gescannt wurden
  useEffect(() => {
    const totalScanned = material.scanned + scannedPallets.length;
    // Wenn alle Paletten gescannt wurden und es mindestens eine neu gescannte Palette gibt
    if (totalScanned >= material.pallets && scannedPallets.length > 0) {
      // Erfolgsfeedback anzeigen und automatisch zurückkehren
      setShowSuccessFeedback(true);
      // Nach 2 Sekunden zurück zur Übersicht
      const timer = setTimeout(() => {
        handleCompleteScan();
        setShowSuccessFeedback(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [material.scanned, scannedPallets.length, material.pallets]);
  const handleScan = () => {
    // Simulate barcode scanning
    if (!scannedCode) {
      setErrorMessage('Bitte geben Sie einen Barcode ein');
      setScanSuccess(false);
      return;
    }
    // Check if this pallet was already scanned
    if (scannedPallets.includes(scannedCode)) {
      setErrorMessage('Diese Palette wurde bereits gescannt');
      setScanSuccess(false);
      return;
    }
    // Simulate successful scan
    setScannedPallets([...scannedPallets, scannedCode]);
    setScanSuccess(true);
    setErrorMessage('');
    // Clear the input after successful scan
    setScannedCode('');
    // Focus the input again for next scan
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
      setScanSuccess(null);
    }, 1500);
  };
  const handleManualComplete = () => {
    if (manualCount <= 0 || manualCount > material.pallets - material.scanned) {
      setErrorMessage(`Bitte geben Sie eine gültige Anzahl ein (1-${material.pallets - material.scanned})`);
      return;
    }
    // Wenn mit der manuellen Eingabe alle Paletten erreicht werden
    if (material.scanned + manualCount >= material.pallets) {
      setShowSuccessFeedback(true);
      // Nach 2 Sekunden zurück zur Übersicht
      setTimeout(() => {
        onScanComplete(manualCount);
        setShowManualEntry(false);
        setManualCount(1);
        setShowSuccessFeedback(false);
        onBack();
      }, 2000);
    } else {
      // Normaler Ablauf wenn nicht alle Paletten erreicht werden
      onScanComplete(manualCount);
      setShowManualEntry(false);
      setManualCount(1);
    }
  };
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleScan();
    }
  };
  const incrementCount = () => {
    if (manualCount < material.pallets - material.scanned) {
      setManualCount(manualCount + 1);
    }
  };
  const decrementCount = () => {
    if (manualCount > 1) {
      setManualCount(manualCount - 1);
    }
  };
  const handleCompleteScan = () => {
    if (scannedPallets.length > 0) {
      onScanComplete(scannedPallets.length);
      setScannedPallets([]);
      // Wenn alle Paletten gescannt wurden, zurück zur Übersicht
      if (material.scanned + scannedPallets.length >= material.pallets) {
        onBack();
      }
    } else {
      setErrorMessage('Es wurden keine Paletten gescannt');
    }
  };
  // Erfolgsfeedback-Komponente
  const SuccessFeedback = () => <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50" data-id="element-596">
      <div className="bg-white rounded-xl p-8 max-w-md text-center" data-id="element-597">
        <div className="bg-green-100 p-3 rounded-full inline-flex mb-4" data-id="element-598">
          <CheckCircleIcon size={48} className="text-green-600" data-id="element-599" />
        </div>
        <h3 className="text-xl font-bold text-gray-800 mb-2" data-id="element-600">
          Bereitstellung abgeschlossen!
        </h3>
        <p className="text-gray-600 mb-4" data-id="element-601">
          Alle Paletten für {material.name} wurden erfolgreich bereitgestellt.
        </p>
        <div className="text-sm text-gray-500" data-id="element-602">
          Sie werden automatisch zur Materialübersicht weitergeleitet...
        </div>
      </div>
    </div>;
  return <div className="border rounded-lg p-6" data-id="element-603">
      {showSuccessFeedback && <SuccessFeedback data-id="element-604" />}
      <div className="flex items-center mb-6" data-id="element-605">
        <button onClick={onBack} className="mr-4 p-2 hover:bg-gray-100 rounded-full" data-id="element-606">
          <ArrowLeftIcon size={20} data-id="element-607" />
        </button>
        <div data-id="element-608">
          <h4 className="text-lg font-semibold text-gray-800" data-id="element-609">
            {material.name} bereitstellen
          </h4>
          <p className="text-sm text-gray-600" data-id="element-610">Artikel-Nr: {material.id}</p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-611">
        <div className="bg-gray-50 p-4 rounded-lg" data-id="element-612">
          <h5 className="font-medium text-gray-700 mb-2" data-id="element-613">
            Materialinformationen
          </h5>
          <div className="space-y-2" data-id="element-614">
            <div className="flex justify-between" data-id="element-615">
              <span className="text-sm text-gray-600" data-id="element-616">Benötigte Menge:</span>
              <span className="text-sm font-medium" data-id="element-617">
                {material.quantity} {material.unit}
              </span>
            </div>
            <div className="flex justify-between" data-id="element-618">
              <span className="text-sm text-gray-600" data-id="element-619">Anzahl Paletten:</span>
              <span className="text-sm font-medium" data-id="element-620">{material.pallets}</span>
            </div>
            <div className="flex justify-between" data-id="element-621">
              <span className="text-sm text-gray-600" data-id="element-622">Bereits gescannt:</span>
              <span className="text-sm font-medium" data-id="element-623">{material.scanned}</span>
            </div>
            <div className="flex justify-between" data-id="element-624">
              <span className="text-sm text-gray-600" data-id="element-625">Noch zu scannen:</span>
              <span className="text-sm font-medium" data-id="element-626">
                {material.pallets - material.scanned}
              </span>
            </div>
            <div className="pt-2 mt-2 border-t border-gray-200" data-id="element-627">
              <div className="flex items-center text-sm text-gray-600" data-id="element-628">
                <MapPinIcon size={14} className="mr-1" data-id="element-629" />
                <span data-id="element-630">
                  Lagerort: {material.location}, Zone {material.zone}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-blue-50 p-4 rounded-lg" data-id="element-631">
          <h5 className="font-medium text-blue-700 mb-2" data-id="element-632">Scanfortschritt</h5>
          <div className="mb-3" data-id="element-633">
            <div className="text-sm text-blue-600 mb-1" data-id="element-634">
              {material.scanned + scannedPallets.length} von {material.pallets}{' '}
              Paletten gescannt
            </div>
            <div className="w-full bg-blue-200 rounded-full h-2.5" data-id="element-635">
              <div className="bg-blue-600 h-2.5 rounded-full" style={{
              width: `${(material.scanned + scannedPallets.length) / material.pallets * 100}%`
            }} data-id="element-636"></div>
            </div>
          </div>
          {scannedPallets.length > 0 && <div className="mb-3" data-id="element-637">
              <p className="text-sm text-blue-700 mb-1" data-id="element-638">
                In dieser Sitzung gescannt:
              </p>
              <div className="flex flex-wrap gap-1" data-id="element-639">
                {scannedPallets.map((code, index) => <span key={index} className="inline-block px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded" data-id="element-640">
                    {code.substring(0, 8)}...
                  </span>)}
              </div>
            </div>}
          {scannedPallets.length > 0}
        </div>
      </div>
      {!showManualEntry ? <div className="border rounded-lg p-4 mb-4" data-id="element-641">
          <h5 className="font-medium text-gray-700 mb-4" data-id="element-642">Paletten scannen</h5>
          <div className="flex flex-col md:flex-row gap-3 mb-4" data-id="element-643">
            <div className="flex-1 relative" data-id="element-644">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-645">
                <div size={18} className="text-gray-400" data-id="element-646" />
              </div>
              <input ref={inputRef} type="text" className={`block w-full pl-10 pr-3 py-3 border ${scanSuccess === true ? 'border-green-500 bg-green-50' : scanSuccess === false ? 'border-red-500 bg-red-50' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`} placeholder="Barcode scannen oder eingeben..." value={scannedCode} onChange={e => setScannedCode(e.target.value)} onKeyDown={handleKeyDown} autoFocus data-id="element-647" />
            </div>
            <button onClick={handleScan} className="bg-[#8DC63F] hover:bg-[#7AB929] text-white py-2 px-4 rounded flex items-center justify-center" data-id="element-648">
              <div size={18} className="mr-2" data-id="element-649" />
              <span data-id="element-650">Scannen</span>
            </button>
          </div>
          {scanSuccess === true && <div className="flex items-center text-green-600 mb-4" data-id="element-651">
              <CheckIcon size={16} className="mr-1" data-id="element-652" />
              <span data-id="element-653">Palette erfolgreich gescannt!</span>
            </div>}
          {scanSuccess === false && <div className="flex items-center text-red-600 mb-4" data-id="element-654">
              <AlertCircleIcon size={16} className="mr-1" data-id="element-655" />
              <span data-id="element-656">{errorMessage}</span>
            </div>}
          <div className="flex justify-center mt-4" data-id="element-657">
            <button onClick={() => setShowManualEntry(true)} className="text-blue-600 hover:text-blue-800 text-sm flex items-center" data-id="element-658">
              <span data-id="element-659">Manuelle Eingabe</span>
            </button>
          </div>
        </div> : <div className="border rounded-lg p-4 mb-4" data-id="element-660">
          <h5 className="font-medium text-gray-700 mb-4" data-id="element-661">Manuelle Eingabe</h5>
          <div className="text-center mb-4" data-id="element-662">
            <p className="text-sm text-gray-600 mb-2" data-id="element-663">
              Geben Sie die Anzahl der bereitgestellten Paletten ein:
            </p>
            <div className="flex items-center justify-center" data-id="element-664">
              <button onClick={decrementCount} className="p-2 border border-gray-300 rounded-l-md" data-id="element-665">
                <MinusIcon size={16} data-id="element-666" />
              </button>
              <input type="number" min="1" max={material.pallets - material.scanned} className="w-16 text-center p-2 border-t border-b border-gray-300" value={manualCount} onChange={e => setManualCount(parseInt(e.target.value) || 1)} data-id="element-667" />
              <button onClick={incrementCount} className="p-2 border border-gray-300 rounded-r-md" data-id="element-668">
                <PlusIcon size={16} data-id="element-669" />
              </button>
            </div>
            {errorMessage && <div className="text-red-600 text-sm mt-2" data-id="element-670">{errorMessage}</div>}
          </div>
          <div className="flex justify-center gap-3" data-id="element-671">
            <button onClick={() => {
          setShowManualEntry(false);
          setErrorMessage('');
        }} className="bg-gray-200 hover:bg-gray-300 text-gray-700 py-2 px-4 rounded" data-id="element-672">
              Abbrechen
            </button>
            <button onClick={handleManualComplete} className="bg-[#8DC63F] hover:bg-[#7AB929] text-white py-2 px-4 rounded" data-id="element-673">
              Bestätigen
            </button>
          </div>
        </div>}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4" data-id="element-674">
        <h5 className="font-medium text-yellow-700 mb-2 flex items-center" data-id="element-675">
          <AlertCircleIcon size={16} className="mr-1" data-id="element-676" />
          <span data-id="element-677">Hinweis</span>
        </h5>
        <p className="text-sm text-yellow-600" data-id="element-678">
          Bitte stellen Sie sicher, dass alle Paletten an den
          Bereitstellungsplatz transportiert werden. Scannen Sie jede Palette
          einzeln, um sie im System zu erfassen.
        </p>
      </div>
    </div>;
}